import React, { useEffect } from "react";
import { useOutletContext } from "react-router-dom";

import { UnifiedDataTable } from "../../../layout/unified-data-table/ui/unified-data-table";
import type { UnifiedDataTableColumnRenderers } from "../../../shared/ui/data-table/model/data-table-contract";
import { summaryRecord } from "../model/summary";

import * as Styled from "./summary.styled";

interface IPatientInfoOutletContext {
  setIsStretchContent: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Summary = () => {
  const outletContext = useOutletContext<
    IPatientInfoOutletContext | undefined
  >();
  const setIsStretchContent = outletContext?.setIsStretchContent;
  const contentScrollerRef = React.useRef<HTMLDivElement | null>(null);
  const [chartAttachmentMessage, setChartAttachmentMessage] = React.useState<
    string | null
  >(null);

  useEffect(() => {
    if (!setIsStretchContent) {
      return;
    }

    setIsStretchContent(true);

    return () => {
      setIsStretchContent(false);
    };
  }, [setIsStretchContent]);

  const columnRenderers = React.useMemo<UnifiedDataTableColumnRenderers>(
    () => ({
      wrappingCell: ({ value }) => (
        <Styled.WrappingCell>{String(value ?? "—")}</Styled.WrappingCell>
      ),
      chartAttachmentMessage: ({ row }) => (
        <Styled.ActionButton
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setChartAttachmentMessage(String(row.message ?? ""));
          }}
        >
          <i className="pi pi-eye" aria-hidden="true" />
          View Message
        </Styled.ActionButton>
      ),
      summaryDocumentView: ({ value }) => (
        <Styled.DocumentLink
          href={typeof value === "string" ? value : "#"}
          aria-label="View document"
        >
          <i className="pi pi-eye" aria-hidden="true" />
          View
        </Styled.DocumentLink>
      ),
    }),
    [],
  );

  return (
    <Styled.Container>
      <Styled.Title>Summary</Styled.Title>
      <UnifiedDataTable
        record={summaryRecord}
        columnRenderers={columnRenderers}
        useSharedSectionNav
        scrollContainerRef={contentScrollerRef}
      />
      <Styled.ThemedDialog
        visible={Boolean(chartAttachmentMessage)}
        modal
        draggable={false}
        resizable={false}
        showHeader
        maskStyle={{
          background: "rgba(59, 68, 76, 0.6)",
        }}
        onHide={() => setChartAttachmentMessage(null)}
      >
        <Styled.DialogWrapper>
          <Styled.DialogTitle>Chart Attachment Message</Styled.DialogTitle>
          <Styled.DialogText>{chartAttachmentMessage}</Styled.DialogText>
          <Styled.DialogActions>
            <Styled.PrimaryDialogButton
              type="button"
              onClick={() => setChartAttachmentMessage(null)}
            >
              OK
            </Styled.PrimaryDialogButton>
          </Styled.DialogActions>
        </Styled.DialogWrapper>
      </Styled.ThemedDialog>
    </Styled.Container>
  );
};
